import { DAMM, dammInfo, FLODESSTATUS } from "../lib/constants";

const NODE_W = 190;
const NODE_H = 56;

function curve(p1, p2, pairIdx) {
    const dx = p2.cx - p1.cx;
    const dy = p2.cy - p1.cy;
    const len = Math.hypot(dx, dy) || 1;
    const sign = pairIdx % 2 === 0 ? 1 : -1;
    const mag = Math.min(90, len * 0.28) * sign * (1 + Math.floor(pairIdx / 2) * 0.5);
    const cx = (p1.cx + p2.cx) / 2 - (dy / len) * mag;
    const cy = (p1.cy + p2.cy) / 2 + (dx / len) * mag;
    return {
        d: `M ${p1.cx} ${p1.cy} Q ${cx} ${cy} ${p2.cx} ${p2.cy}`,
        mid: { x: 0.25 * p1.cx + 0.5 * cx + 0.25 * p2.cx, y: 0.25 * p1.cy + 0.5 * cy + 0.25 * p2.cy },
        c: { x: cx, y: cy },
    };
}

function resolveLabelOffsets(items, obstacles = []) {
    const placed = [...obstacles];
    const offsets = {};
    const sorted = [...items].sort((a, b) => a.x - b.x || a.y - b.y);
    for (const item of sorted) {
        let y = item.y;
        let moved = true;
        while (moved) {
            moved = false;
            for (const p of placed) {
                const overlapX = Math.abs(item.x - p.x) < (item.w + p.w) / 2 + 8;
                const overlapY = y < p.y + p.h + 6 && y + item.h + 6 > p.y;
                if (overlapX && overlapY) {
                    y = p.y + p.h + 6;
                    moved = true;
                }
            }
        }
        placed.push({ ...item, y });
        offsets[item.key] = y - item.y;
    }
    return offsets;
}

export default function FlowMap({ graph, layers, selection, onSelect, presentation = false }) {
    const nodes = graph.nodes || [];
    const flows = graph.flows || [];
    const bottlenecks = (graph.bottlenecks || []).filter((b) => b.status !== "avvisad");
    const buoys = graph.buoys || [];

    const pts = nodes.map((n) => ({ ...n, cx: n.x + NODE_W / 2, cy: n.y + NODE_H / 2 }));
    const byId = Object.fromEntries(pts.map((p) => [p.id, p]));

    const xs = pts.map((p) => p.cx);
    const ys = pts.map((p) => p.cy);
    const minX = Math.min(...xs, 0) - 170;
    const maxX = Math.max(...xs, 900) + 170;
    const minY = Math.min(...ys, 0) - 130;
    const maxY = Math.max(...ys, 500) + 130;

    const pairCounter = {};
    const geoms = flows.map((f) => {
        const p1 = byId[f.source];
        const p2 = byId[f.target];
        if (!p1 || !p2) return null;
        const key = [f.source, f.target].sort().join("|");
        const idx = pairCounter[key] || 0;
        pairCounter[key] = idx + 1;
        const cv = curve(p1, p2, idx);
        const t = 0.72;
        const brk = {
            x: (1 - t) ** 2 * p1.cx + 2 * (1 - t) * t * cv.c.x + t * t * p2.cx,
            y: (1 - t) ** 2 * p1.cy + 2 * (1 - t) * t * cv.c.y + t * t * p2.cy,
        };
        return { f, idx, brk, ...cv };
    }).filter(Boolean).map((g, gi, arr) => ({
        ...g,
        slot: arr.slice(0, gi).filter((o) => Math.abs(o.mid.x - g.mid.x) < 60 && Math.abs(o.mid.y - g.mid.y) < 60).length,
    }));

    const damsForFlow = (fid) => bottlenecks.filter((b) => b.target_type === "flode" && b.target_id === fid);
    const damsForNode = (nid) => bottlenecks.filter((b) => b.target_type === "nod" && b.target_id === nid);

    const labelCandidates = [];
    const nodeObstacles = pts.map((p) => ({ x: p.cx, y: p.cy - NODE_H / 2, w: NODE_W, h: NODE_H }));
    const pushLabel = (key, x, baseline, text, fontSize) => {
        labelCandidates.push({
            key, x, y: baseline - fontSize,
            w: Math.max(28, String(text).length * fontSize * 0.62),
            h: fontSize + 5,
        });
    };
    geoms.forEach(({ f, mid, idx, slot }) => {
        pushLabel(`flow-${f.id}`, mid.x, mid.y - 12 - (idx % 3) * 13, f.label || "", 11);
        const flowDams = damsForFlow(f.id);
        flowDams.forEach((b, i) => {
            const info = dammInfo(b);
            const x = mid.x + i * 30 - (flowDams.length - 1) * 15;
            const y = mid.y + 10 + slot * 18;
            pushLabel(`dam-${b.id}`, x, y + 30, `${info.label}${b.status === "foreslagen" ? " (förslag)" : ""}`, 9);
        });
    });
    const labelOffsets = resolveLabelOffsets(labelCandidates, nodeObstacles);
    const labelMetrics = Object.fromEntries(labelCandidates.map((l) => [l.key, l]));

    const buoyVisible = (b) => {
        const persp = b.insights?.perspektiv;
        if (!persp || !persp.length) return true;
        return persp.some((p) => layers?.[p]);
    };

    const nodeDim = (n) => (n?.perspectives?.length || 0) > 0 && !n.perspectives.some((p) => layers?.[p]);

    return (
        <div className={`relative w-full h-full canvas-bg overflow-hidden ${presentation ? "" : ""}`} data-testid="flow-map">
            <svg
                viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`}
                className="w-full h-full"
                preserveAspectRatio="xMidYMid meet"
            >
                <defs>
                    <marker id="flow-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                        <path d="M 0 1 L 9 5 L 0 9 z" fill="#38BDF8" opacity="0.9" />
                    </marker>
                </defs>

                {geoms.map(({ f, d, mid, idx, brk, slot }) => {
                    const w = 1.5 + f.volume * 1.4;
                    const dur = `${Math.max(1.2, 6.5 - f.tempo)}s`;
                    const sel = selection?.kind === "flow" && selection.id === f.id;
                    const st = FLODESSTATUS[f.flodesstatus] || FLODESSTATUS.nar_fram;
                    const broken = f.flodesstatus === "nar_inte_fram";
                    const dim = nodeDim(byId[f.source]) || nodeDim(byId[f.target]);
                    const labelY = mid.y - 12 - (idx % 3) * 13 + (labelOffsets[`flow-${f.id}`] || 0);
                    const flowLabel = labelMetrics[`flow-${f.id}`];
                    return (
                        <g key={f.id} style={{ opacity: dim ? 0.1 : 1, transition: "opacity 600ms ease" }} pointerEvents={dim ? "none" : undefined}>
                            <path d={d} fill="none" stroke={`${st.color}24`} strokeWidth={w + 8} strokeLinecap="round" />
                            <path d={d} fill="none" stroke={sel ? "#7DD3FC" : `${st.color}59`} strokeWidth={w} strokeLinecap="round" />
                            <path
                                d={d} fill="none" stroke={st.color} strokeWidth={Math.max(1.5, w - 2)}
                                strokeDasharray={broken ? "4 10" : "2 16"} strokeLinecap="round"
                                className="flow-dash" style={{ animationDuration: dur }}
                                markerEnd={broken ? undefined : "url(#flow-arrow)"}
                            />
                            {broken && (
                                <g transform={`translate(${brk.x}, ${brk.y})`} data-testid={`flow-break-${f.id}`}>
                                    <circle r="8" fill="#0A121D" stroke="#EF4444" strokeWidth="1.5" />
                                    <line x1="-3.5" y1="-3.5" x2="3.5" y2="3.5" stroke="#EF4444" strokeWidth="1.5" />
                                    <line x1="3.5" y1="-3.5" x2="-3.5" y2="3.5" stroke="#EF4444" strokeWidth="1.5" />
                                    <title>Informationen når inte fram</title>
                                </g>
                            )}
                            <g pointerEvents="none" data-testid={`flow-label-${f.id}`}>
                                <rect x={mid.x - flowLabel.w / 2 - 5} y={labelY - 13} width={flowLabel.w + 10} height={flowLabel.h + 4} rx="5"
                                    fill="#060B11" opacity="0.88" stroke="rgba(56,189,248,0.12)" />
                                <text x={mid.x} y={labelY} textAnchor="middle" fontSize="11" fill="#94A3B8" fontFamily="'IBM Plex Mono', monospace">
                                    {f.label}
                                </text>
                            </g>
                            <path
                                d={d} fill="none" stroke="transparent" strokeWidth={w + 18}
                                style={{ cursor: onSelect ? "pointer" : "default", pointerEvents: "stroke" }}
                                onClick={() => onSelect?.({ kind: "flow", id: f.id })}
                                data-testid={`flow-path-${f.id}`}
                            />
                            {damsForFlow(f.id).map((b, i) => {
                                const info = dammInfo(b);
                                const x = mid.x + i * 30 - (damsForFlow(f.id).length - 1) * 15;
                                const y = mid.y + 10 + slot * 18;
                                const damLabel = labelMetrics[`dam-${b.id}`];
                                return (
                                    <g key={b.id} transform={`translate(${x}, ${y})`}
                                        style={{ cursor: onSelect ? "pointer" : "default" }}
                                        onClick={(e) => { e.stopPropagation(); onSelect?.({ kind: "bottleneck", id: b.id }); }}
                                        data-testid={`dam-marker-${b.id}`}>
                                        {b.status === "foreslagen" && (
                                            <circle r="20" fill="none" stroke={info.color} strokeWidth="1.5" strokeDasharray="4 4" className="damm-pulse" />
                                        )}
                                        <circle r="14" fill="#0A121D" stroke={info.color} strokeWidth="2" />
                                        <rect x="-6" y="-5" width="3" height="10" fill={info.color} rx="0.5" />
                                        <rect x="-1.5" y="-7" width="3" height="14" fill={info.color} rx="0.5" />
                                        <rect x="3" y="-5" width="3" height="10" fill={info.color} rx="0.5" />
                                        <g pointerEvents="none" data-testid={`dam-label-${b.id}`}>
                                            <rect x={-damLabel.w / 2 - 4} y={30 + (labelOffsets[`dam-${b.id}`] || 0) - 11} width={damLabel.w + 8} height={damLabel.h + 4} rx="4"
                                                fill="#060B11" opacity="0.88" stroke={`${info.color}33`} />
                                            <text y={30 + (labelOffsets[`dam-${b.id}`] || 0)} textAnchor="middle" fontSize="9" fill={info.color} fontFamily="'IBM Plex Mono', monospace">
                                                {info.label}{b.status === "foreslagen" ? " (förslag)" : ""}
                                            </text>
                                        </g>
                                        <title>{info.label} · allvarlighet {b.allvarlighet}/5 · {b.notering}</title>
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}

                {pts.map((p) => {
                    const dams = damsForNode(p.id);
                    const sel = selection?.kind === "node" && selection.id === p.id;
                    return (
                        <g key={p.id}
                            style={{ cursor: onSelect ? "pointer" : "default", opacity: nodeDim(p) ? 0.12 : 1, transition: "opacity 600ms ease" }}
                            pointerEvents={nodeDim(p) ? "none" : undefined}
                            onClick={() => onSelect?.({ kind: "node", id: p.id })}
                            data-testid={`flow-node-${p.id}`}>
                            <rect x={p.cx - NODE_W / 2} y={p.cy - NODE_H / 2} width={NODE_W} height={NODE_H} rx="10"
                                fill="#101B2B" stroke={sel ? "#38BDF8" : "rgba(56,189,248,0.15)"} strokeWidth={sel ? 2 : 1} />
                            <rect x={p.cx - NODE_W / 2} y={p.cy - NODE_H / 2 + 8} width="4" height={NODE_H - 16} rx="2" fill="#38BDF8" opacity="0.7" />
                            <text x={p.cx} y={p.cy - 2} textAnchor="middle" fontSize="13" fontWeight="600" fill="#F1F5F9" fontFamily="'IBM Plex Sans', sans-serif">
                                {p.label.length > 24 ? p.label.slice(0, 23) + "…" : p.label}
                            </text>
                            <text x={p.cx} y={p.cy + 15} textAnchor="middle" fontSize="9" fill="#64748B" fontFamily="'IBM Plex Mono', monospace" letterSpacing="2">
                                {p.type.toUpperCase()}{p.antal_anstallda ? ` · ${p.antal_anstallda} ANSTÄLLDA` : ""}
                            </text>
                            {dams.map((b, i) => {
                                const info = dammInfo(b);
                                return (
                                    <g key={b.id} transform={`translate(${p.cx + NODE_W / 2 - 4}, ${p.cy - NODE_H / 2 + 4 + i * 24})`}
                                        onClick={(e) => { e.stopPropagation(); onSelect?.({ kind: "bottleneck", id: b.id }); }}
                                        data-testid={`dam-marker-${b.id}`}>
                                        {b.status === "foreslagen" && <circle r="15" fill="none" stroke={info.color} strokeWidth="1.5" strokeDasharray="4 4" className="damm-pulse" />}
                                        <circle r="10" fill="#0A121D" stroke={info.color} strokeWidth="2" />
                                        <rect x="-4" y="-3.5" width="2.2" height="7" fill={info.color} />
                                        <rect x="-1" y="-5" width="2.2" height="10" fill={info.color} />
                                        <rect x="2" y="-3.5" width="2.2" height="7" fill={info.color} />
                                        <title>{info.label} · allvarlighet {b.allvarlighet}/5</title>
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}

                {buoys.filter((b) => b.node_id && byId[b.node_id]).map((b) => {
                    const p = byId[b.node_id];
                    const x = p.cx + NODE_W / 2 + 46;
                    const y = p.cy - NODE_H / 2 - 26;
                    const dim = !buoyVisible(b);
                    return (
                        <g key={b.id} transform={`translate(${x}, ${y})`}
                            style={{ cursor: onSelect ? "pointer" : "default", opacity: dim ? 0.2 : 1, transition: "opacity 600ms ease" }}
                            onClick={() => onSelect?.({ kind: "buoy", id: b.id })}
                            data-testid={`buoy-marker-${b.id}`}>
                            <circle r="16" fill="none" stroke="#10B981" strokeWidth="1.5" className="buoy-pulse" />
                            <circle r="7" fill="#0A121D" stroke="#10B981" strokeWidth="2" />
                            <circle r="2.5" fill="#10B981" />
                            <text y="28" textAnchor="middle" fontSize="9" fill="#10B981" fontFamily="'IBM Plex Mono', monospace">
                                {b.etikett}
                            </text>
                            <title>{b.etikett} · {b.status}</title>
                        </g>
                    );
                })}

                {flows.length === 0 && (
                    <text x={(minX + maxX) / 2} y={(minY + maxY) / 2} textAnchor="middle" fontSize="14" fill="#475569" fontFamily="'IBM Plex Sans', sans-serif">
                        Inga flöden ännu – lägg till flöden för att se havsströmmarna.
                    </text>
                )}
            </svg>

            <div className="absolute left-4 bottom-4 glass-panel rounded-xl px-4 py-3" data-testid="flow-legend">
                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Dammar</p>
                <div className="space-y-1">
                    {Object.entries(DAMM).map(([id, d]) => (
                        <div key={id} className="flex items-center gap-2 text-[11px] text-slate-300">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                            {d.label}
                        </div>
                    ))}
                    <div className="flex items-center gap-2 text-[11px] text-slate-300 pt-1 border-t border-border mt-1">
                        <span className="w-2.5 h-2.5 rounded-full bg-buoy" />
                        Boj (datainsamling)
                    </div>
                    <div className="pt-1 border-t border-border mt-1 space-y-1">
                        {Object.entries(FLODESSTATUS).map(([id, s]) => (
                            <div key={id} className="flex items-center gap-2 text-[11px] text-slate-300">
                                <span className="w-4 border-t-2" style={{ borderColor: s.color, borderStyle: id === "nar_inte_fram" ? "dashed" : "solid" }} />
                                {s.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
